/**
 * Encerramento do caso da CAT — issues #525 e #420.
 *
 * Vale para toda CAT não cancelada. Pede a data, os dias de afastamento (só quando a CAT marca
 * afastamento) e se o caso resultou em incapacidade definitiva: esses números entram nos quadros
 * de acidentes e de doenças do PCMSO e do Relatório Analítico. "Reabrir caso" apaga só a data;
 * os dias e a incapacidade ficam gravados e voltam preenchidos no próximo encerramento.
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { catService } from '../../../services/catService';
import { AccidentType, Cat } from '../../../types/cat';

const TEXTO_ACIDENTE =
  'Informe quando o caso terminou e quantos dias o trabalhador ficou afastado. Esses números entram no quadro de acidentes do PCMSO e do Relatório Analítico.';
const TEXTO_DOENCA =
  'A partir da data de encerramento, a doença deixa de contar como caso existente no Relatório Analítico. Os dias e a incapacidade entram no quadro de doenças ocupacionais.';

type Incapacidade = '' | 'S' | 'N';

interface EncerramentoDoCasoDialogProps {
  open: boolean;
  cat: Cat;
  onClose: () => void;
  onSaved: () => void;
}

export const EncerramentoDoCasoDialog: React.FC<EncerramentoDoCasoDialogProps> = ({
  open,
  cat,
  onClose,
  onSaved,
}) => {
  const [data, setData] = useState('');
  const [dias, setDias] = useState('');
  const [incapacidade, setIncapacidade] = useState<Incapacidade>('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const temAfastamento = cat.willBeAbsent === 'S';
  const ehDoenca = cat.accidentType === AccidentType.OCCUPATIONAL_DISEASE;

  useEffect(() => {
    if (open) {
      setData(cat.caseClosedAt ? cat.caseClosedAt.slice(0, 10) : '');
      setDias(cat.lostWorkDays === null || cat.lostWorkDays === undefined ? '' : String(cat.lostWorkDays));
      setIncapacidade(cat.permanentIncapacity ?? '');
      setErro(null);
    }
  }, [open, cat.caseClosedAt, cat.lostWorkDays, cat.permanentIncapacity]);

  const enviar = async (envio: () => Promise<unknown>) => {
    setSalvando(true);
    setErro(null);
    try {
      await envio();
      onSaved();
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Erro ao registrar o encerramento do caso');
    } finally {
      setSalvando(false);
    }
  };

  const salvar = () => {
    if (incapacidade === '') {
      return;
    }
    void enviar(() =>
      catService.recordCaseClosure(cat.id, {
        caseClosedAt: data,
        lostWorkDays: temAfastamento ? Number(dias) : null,
        permanentIncapacity: incapacidade,
      }),
    );
  };

  const reabrir = () => {
    void enviar(() => catService.recordCaseClosure(cat.id, { caseClosedAt: null }));
  };

  const podeSalvar = data !== '' && incapacidade !== '' && (!temAfastamento || dias !== '');

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Registrar encerramento do caso</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{ehDoenca ? TEXTO_DOENCA : TEXTO_ACIDENTE}</DialogContentText>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            fullWidth
            required
            type="date"
            label="Data de encerramento"
            value={data}
            onChange={(event) => setData(event.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={salvando}
          />
          {temAfastamento && (
            <TextField
              fullWidth
              required
              type="number"
              label="Dias de afastamento"
              value={dias}
              onChange={(event) => setDias(event.target.value)}
              inputProps={{ min: 0, max: 36500, step: 1 }}
              helperText='Dias corridos em que o trabalhador não trabalhou por causa deste caso. Pedido porque a CAT marca "Afastamento: Sim".'
              disabled={salvando}
            />
          )}
          <TextField
            select
            fullWidth
            required
            label="Resultou em incapacidade definitiva?"
            value={incapacidade}
            onChange={(event) => setIncapacidade(event.target.value as Incapacidade)}
            helperText="Sim, quando o trabalhador não pode mais exercer a função de forma permanente."
            disabled={salvando}
          >
            <MenuItem value="N">Não</MenuItem>
            <MenuItem value="S">Sim</MenuItem>
          </TextField>
        </Stack>
        {erro && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {erro}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={salvando}>
          Voltar
        </Button>
        {cat.caseClosedAt && (
          <Button onClick={reabrir} color="warning" disabled={salvando}>
            Reabrir caso
          </Button>
        )}
        <Button onClick={salvar} variant="contained" disabled={salvando || !podeSalvar}>
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EncerramentoDoCasoDialog;
